import React from "react";
import { useQuery } from "@tanstack/react-query";
import { isAllBusiness, useAuthenticatedAPI } from "@/api";
import { useBusinessContext } from "@/hooks/useBusinessContext";
import { useGetBusinesses } from "@/hooks/useGetBusinesses";
import { SEmbedLoadingState } from "./SEmbedLoadingState";
import { SEmbedSection, useSEmbed } from "./useSEmbed";
import type { SEmbedConfig } from "@norma-bi/bi-api";
import { useSFullAppSession } from "./SFullAppContext";
import { useSFullApp } from "@/hooks/useSFullApp";

export function SEmbedHome() {
  const fullApp = useSFullApp();
  if (fullApp.isLoading) return <SEmbedLoadingState />;
  return fullApp.enabled ? <SFullAppPage /> : <SEmbedPage section="chat" />;
}

export function SFullAppPage() {
  const { embedSession, isSwitching, switchError, switchBusiness, canPostToken } =
    useSFullAppSession();
  const lastConfig = React.useRef<SEmbedConfig | undefined>(undefined);
  if (embedSession.data?.config) lastConfig.current = embedSession.data.config;
  const { isReady, iframeProps } = useSEmbed({
    embedSession,
    section: "chat",
    fullApp: true,
    configFallback: lastConfig.current,
    onBusinessSelect: switchBusiness,
    canPostToken,
  });
  const error = switchError ?? (embedSession.isError ? embedSession.error : null);
  const staleContext =
    error && "response" in error && (error.response as Response | undefined)?.status === 409;
  return (
    <div className="relative h-full w-full min-h-0 min-w-0">
      {error ? (
        <div
          role="alert"
          className="absolute inset-0 z-10 flex items-center justify-center bg-lightGray p-8 text-center"
        >
          {staleContext
            ? "Your data context has changed. Refresh this page to continue."
            : (switchError?.message ??
              "Failed to load S. Please refresh this page or contact support.")}
        </div>
      ) : (
        (!isReady || embedSession.isFetching || isSwitching) && (
          <div className="absolute inset-0 z-10">
            <SEmbedLoadingState />
          </div>
        )
      )}
      {lastConfig.current && (
        <iframe {...iframeProps} title="Foodics AI" className="h-full w-full border-0" />
      )}
    </div>
  );
}

export function SEmbedPage({ section }: { section: SEmbedSection }) {
  const api = useAuthenticatedAPI();
  const businesses = useGetBusinesses();
  const { business } = useBusinessContext();
  const businessId = business?.id;

  const businessIds = React.useMemo(() => {
    if (!businessId) {
      return [];
    }
    if (isAllBusiness(businessId)) {
      return (businesses.data ?? []).map((b) => b.id);
    }
    return [businessId];
  }, [businessId, businesses.data]);

  const embedSession = useQuery({
    queryKey: ["sEmbedSession", businessIds],
    queryFn: () =>
      api.createSEmbedSession({
        CreateSEmbedSessionRequest: { businessIds },
      }),
    enabled: businessIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Hook owns iframe URL/origin derivation, bootstrap token payload,
  // and race-resistant cross-window handshake logic.
  const { isReady, iframeProps } = useSEmbed({
    embedSession,
    section,
  });

  if (
    !businessId ||
    (isAllBusiness(businessId) && !businesses.isSuccess) ||
    embedSession.isLoading
  ) {
    return (
      <div className="px-4 pt-4 w-full h-full">
        <SEmbedLoadingState />
      </div>
    );
  }

  if (embedSession.isError || !embedSession.data) {
    return (
      <div className="flex justify-center h-full pt-48">
        <span className="text-errorContent italic select-none">Failed to load S embed</span>
      </div>
    );
  }

  return (
    <div className="relative px-4 pt-4 w-full h-full">
      {!isReady && (
        <div className="absolute inset-x-4 bottom-0 top-4 z-10">
          <SEmbedLoadingState />
        </div>
      )}
      <iframe {...iframeProps} className="w-full h-full rounded-t-lg border-0" />
    </div>
  );
}
